import { useEffect, useRef } from 'react';
import BundleMonitor, { BundleStats } from '../utils/bundleMonitor';
import { useToastContext } from '../contexts/ToastContext';

interface UseBundleMonitorProps {
  enabled?: boolean;
  intervalMs?: number;
  thresholds?: {
    warningSize?: number;
    errorSize?: number;
    maxChunks?: number;
  };
}

export const useBundleMonitor = ({
  enabled = process.env.NODE_ENV === 'development',
  intervalMs = 5 * 60 * 1000, // 5 minutes
  thresholds = {}
}: UseBundleMonitorProps = {}) => {
  const { showWarning, showError } = useToastContext();
  const monitorRef = useRef<BundleMonitor | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!enabled) return;

    // Initialize monitor
    monitorRef.current = new BundleMonitor(thresholds);
    
    // Set up alert callback
    monitorRef.current.setAlertCallback((level, message) => {
      if (level === 'error') {
        showError('Bundle Size Alert', message, 10000); // Show for 10 seconds
      } else {
        showWarning('Bundle Performance Warning', message, 8000);
      }
    });

    // Start monitoring
    cleanupRef.current = monitorRef.current.startPeriodicMonitoring(intervalMs);

    // Initial check
    monitorRef.current.monitorBundle();

    // Cleanup on unmount
    return () => {
      cleanupRef.current?.();
    };
  }, [enabled, intervalMs, showWarning, showError, thresholds]);

  // Manual trigger for bundle analysis
  const analyzeNow = async (): Promise<BundleStats | null> => {
    if (!monitorRef.current) return null;
    
    try {
      const stats = await monitorRef.current.analyzeBundleSize();
      const health = monitorRef.current.checkBundleHealth(stats);
      
      if (health.level === 'ok') {
        showWarning('Bundle Analysis', health.message, 3000);
      } else if (health.level === 'warning') {
        showWarning('Bundle Warning', health.message, 5000);
      } else {
        showError('Bundle Error', health.message, 7000);
      }
      
      return stats;
    } catch (error) {
      showError('Analysis Failed', 'Unable to analyze bundle size');
      return null;
    }
  };

  // Get bundle history
  const getBundleHistory = (): BundleStats[] => {
    return monitorRef.current?.getBundleHistory() || [];
  };

  return {
    analyzeNow,
    getBundleHistory,
    isEnabled: enabled
  };
};